import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Typography,
  Divider,
  CircularProgress,
  TablePagination,
  Box,
  Chip
} from '@mui/material';
import { Person as PersonIcon, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import api from '../../../services/api';

const TicketAssignModal = ({ open, onClose, onAssign }) => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);

  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 10,
    total: 0
  });

  useEffect(() => {
    if (open) {
      fetchEmployees();
      setSelectedEmployee(null);
    }
  }, [open, pagination.page, pagination.rowsPerPage]);

  const handleAssign = async () => {
    if (!selectedEmployee) return;
    setLoading(true);
    try {
      await onAssign(selectedEmployee.id);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedEmployee(null);
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        size: pagination.rowsPerPage,
        status: 'active'
      };

      const response = await api.get('/v1/api/users/employees', { params });
      setEmployees(response.data.content || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.totalElements || 0
      }));
    } catch (error) {
      console.error('Failed to load employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event) => {
    setPagination(prev => ({
      ...prev,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'primary.contrastText',
          py: 2
        }}>
        <Typography variant="h6" fontWeight="bold">
          Assign Ticket to Employee
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {selectedEmployee && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1">
              Selected: <strong>{selectedEmployee.firstName} {selectedEmployee.lastName}</strong>
            </Typography>
            <Button
              size="small"
              color="error"
              startIcon={<CloseIcon />}
              onClick={handleClearSelection}
            >
              Clear
            </Button>
          </Box>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <List>
              {employees.map((employee) => (
                <React.Fragment key={employee.id}>
                  <ListItem
                    button
                    onClick={() => setSelectedEmployee(employee)}
                    sx={{
                      backgroundColor: selectedEmployee?.id === employee.id ? 'action.selected' : 'inherit',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar src={employee.avatar}>
                        {employee.avatar ? null : `${employee.firstName.charAt(0)}${employee.lastName.charAt(0)}`}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${employee.firstName} ${employee.lastName}`}
                      secondary={
                        <>
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.primary"
                            display="block"
                          >
                            {employee.position}
                          </Typography>
                          {employee.email}
                        </>
                      }
                    />
                    {selectedEmployee?.userUuid === employee.userUuid && (
                      <CheckIcon color="primary" />
                    )}
                  </ListItem>
                  <Divider variant="inset" component="li" />
                </React.Fragment>
              ))}
            </List>
            <TablePagination
              component="div"
              count={pagination.total}
              rowsPerPage={pagination.rowsPerPage}
              page={pagination.page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          color="primary"
          variant="contained"
          disabled={!selectedEmployee || loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketAssignModal;